// Server response interface (snake_case)
interface ServerFeedbackRecord {
  id: string;
  participant_id?: string;
  user_id: string;
  session_id?: string;
  feedback_text: string;
  character_count: number;
  submission_context: 'demographics' | 'annotation' | 'completion';
  is_processed: number; // SQLite boolean (0 = false, 1 = true)
  processed_at?: string;
  processed_by?: string;
  created_at: string;
  updated_at: string;
  participantUsername?: string;
  userUsername: string;
  sessionProgress?: number;
}

// Client interface (camelCase)
export interface FeedbackRecord {
  id: string;
  participantId?: string;
  userId: string;
  sessionId?: string;
  feedbackText: string;
  characterCount: number;
  submissionContext: 'demographics' | 'annotation' | 'completion';
  isProcessed: boolean;
  processedAt?: string;
  processedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AdminFeedbackRecord extends FeedbackRecord {
  participantUsername?: string;
  userUsername: string;
  sessionProgress?: number;
}

export interface FeedbackStats {
  totalCount: number;
  processedCount: number;
  unprocessedCount: number;
  averageLength: number;
  contextBreakdown: Record<string, number>;
}

export interface FeedbackFilters {
  processed?: boolean;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  context?: string;
  participantId?: string;
  limit?: number;
  offset?: number;
}

export interface GetFeedbackResponse {
  feedback: AdminFeedbackRecord[];
  stats: FeedbackStats;
  totalCount: number;
  hasMore: boolean;
}

class FeedbackService {
  private baseUrl = '/api/admin/feedback';

  // Convert server response to client format
  private mapServerToClient(serverRecord: ServerFeedbackRecord): AdminFeedbackRecord {
    return {
      id: serverRecord.id,
      participantId: serverRecord.participant_id,
      userId: serverRecord.user_id,
      sessionId: serverRecord.session_id,
      feedbackText: serverRecord.feedback_text,
      characterCount: serverRecord.character_count,
      submissionContext: serverRecord.submission_context,
      isProcessed: serverRecord.is_processed === 1,
      processedAt: serverRecord.processed_at,
      processedBy: serverRecord.processed_by,
      createdAt: serverRecord.created_at,
      updatedAt: serverRecord.updated_at,
      participantUsername: serverRecord.participantUsername,
      userUsername: serverRecord.userUsername,
      sessionProgress: serverRecord.sessionProgress
    };
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('auth-token');
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Get all feedback with filtering and pagination
   */
  async getFeedback(filters: FeedbackFilters = {}): Promise<GetFeedbackResponse> {
    const searchParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, value.toString());
      }
    });

    const queryString = searchParams.toString();
    const endpoint = queryString ? `?${queryString}` : '';
    
    const response = await this.makeRequest<{
      feedback: ServerFeedbackRecord[];
      stats: FeedbackStats;
      totalCount: number;
      hasMore: boolean;
    }>(endpoint);

    return {
      feedback: response.feedback.map(record => this.mapServerToClient(record)),
      stats: response.stats,
      totalCount: response.totalCount,
      hasMore: response.hasMore
    };
  }

  /**
   * Mark feedback as processed/unprocessed
   */
  async markAsProcessed(feedbackId: string, isProcessed: boolean): Promise<{ success: boolean }> {
    return this.makeRequest<{ success: boolean }>(`/${feedbackId}/process`, {
      method: 'PATCH',
      body: JSON.stringify({ isProcessed }),
    });
  }

  /**
   * Bulk process multiple feedback items
   */
  async bulkProcess(feedbackIds: string[], isProcessed: boolean): Promise<{ processedCount: number }> {
    return this.makeRequest<{ processedCount: number }>('/bulk-process', {
      method: 'PATCH',
      body: JSON.stringify({ feedbackIds, isProcessed }),
    });
  }

  /**
   * Get feedback statistics
   */
  async getStats(): Promise<FeedbackStats> {
    const response = await this.getFeedback({ limit: 0 });
    return response.stats;
  }
}

export const feedbackService = new FeedbackService();